import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from '@/infrastructure/api-client';
import type {
  FunnelListResponse,
  FunnelSummary,
  FunnelDetail,
  Folder,
  Tag,
  GraphDocument,
  SaveGraphResponse,
} from '@/contracts/funnel';
import type {
  TemplateListResponse,
  TemplateDetail,
  TemplateSummary,
  TemplateType,
  InstantiateTemplateRequest,
  CustomTemplateCreateRequest,
  CustomTemplateUpdateRequest,
} from '@/contracts/templates';
import type { FunnelDraft } from '@/domain/ai/funnel-draft';

/**
 * SPEC-003 T035: components never call `fetch`/raw endpoints directly —
 * only through this typed client.
 */
export function listFunnels(query: URLSearchParams): Promise<FunnelListResponse> {
  const qs = query.toString();
  return apiGet<FunnelListResponse>(`/api/funnels${qs ? `?${qs}` : ''}`);
}

export function getFunnel(id: string): Promise<{ funnel: FunnelDetail }> {
  return apiGet(`/api/funnels/${id}`);
}

export function createFunnel(name?: string): Promise<{ funnel: FunnelSummary }> {
  return apiPost('/api/funnels', name ? { name } : {});
}

export function generateFunnelDraft(description: string, signal?: AbortSignal): Promise<{ draft: FunnelDraft }> {
  return apiPost('/api/ai/funnel-drafts', { description }, signal);
}

export function createFunnelFromDraft(draft: FunnelDraft, name: string): Promise<{ funnel: FunnelSummary }> {
  return apiPost('/api/funnels/from-ai-draft', { draft, name });
}

export function updateFunnel(
  id: string,
  fields: { name?: string; description?: string | null; folderId?: string | null; isFavorite?: boolean },
): Promise<{ funnel: FunnelSummary }> {
  return apiPatch(`/api/funnels/${id}`, fields);
}

export function saveFunnelGraph(
  id: string,
  expectedRevision: number,
  graph: GraphDocument,
  signal?: AbortSignal,
): Promise<SaveGraphResponse> {
  return apiPut(`/api/funnels/${id}/graph`, { expectedRevision, graph }, signal);
}

export function deleteFunnel(id: string): Promise<{ ok: true }> {
  return apiDelete(`/api/funnels/${id}`);
}

export function recordFunnelExport(
  id: string,
  format: 'png' | 'pdf' | 'json',
): Promise<{ ok: true }> {
  return apiPost(`/api/funnels/${id}/export-event`, { format });
}

export function duplicateFunnel(id: string): Promise<{ funnel: FunnelSummary }> {
  return apiPost(`/api/funnels/${id}/duplicate`);
}

export function archiveFunnel(id: string): Promise<{ ok: true }> {
  return apiPost(`/api/funnels/${id}/archive`);
}

export function unarchiveFunnel(id: string): Promise<{ ok: true }> {
  return apiPost(`/api/funnels/${id}/unarchive`);
}

export function listFolders(): Promise<{ items: Folder[] }> {
  return apiGet('/api/folders');
}

export function createFolder(name: string): Promise<{ folder: Folder }> {
  return apiPost('/api/folders', { name });
}

export function renameFolder(id: string, name: string): Promise<{ folder: Folder }> {
  return apiPatch(`/api/folders/${id}`, { name });
}

export function deleteFolder(id: string): Promise<{ ok: true }> {
  return apiDelete(`/api/folders/${id}`);
}

export function listTags(): Promise<{ items: Tag[] }> {
  return apiGet('/api/tags');
}

export function createTag(name: string): Promise<{ tag: Tag }> {
  return apiPost('/api/tags', { name });
}

export function deleteTag(id: string): Promise<{ ok: true }> {
  return apiDelete(`/api/tags/${id}`);
}

export function assignFunnelTag(funnelId: string, tagId: string): Promise<{ ok: true }> {
  return apiPost(`/api/funnels/${funnelId}/tags/${tagId}`);
}

export function removeFunnelTag(funnelId: string, tagId: string): Promise<{ ok: true }> {
  return apiDelete(`/api/funnels/${funnelId}/tags/${tagId}`);
}

// --- Templates (SPEC-010) ----------------------------------------------------

export function listTemplates(): Promise<TemplateListResponse> {
  return apiGet('/api/templates');
}

export function getTemplateDetail(type: TemplateType, id: string): Promise<{ template: TemplateDetail }> {
  return apiGet(`/api/templates/${type}/${id}`);
}

export function instantiateTemplate(
  type: TemplateType,
  id: string,
  input: InstantiateTemplateRequest,
): Promise<{ funnel: FunnelSummary }> {
  return apiPost(`/api/templates/${type}/${id}/instantiate`, input);
}

export function createCustomTemplate(
  input: CustomTemplateCreateRequest,
): Promise<{ template: TemplateSummary }> {
  return apiPost('/api/custom-templates', input);
}

export function updateCustomTemplate(
  id: string,
  input: CustomTemplateUpdateRequest,
): Promise<{ template: TemplateSummary }> {
  return apiPatch(`/api/custom-templates/${id}`, input);
}

export function deleteCustomTemplate(id: string): Promise<{ ok: true }> {
  return apiDelete(`/api/custom-templates/${id}`);
}

export function replaceCustomTemplateFromFunnel(
  id: string,
  funnelId: string,
): Promise<{ template: TemplateSummary }> {
  return apiPost(`/api/custom-templates/${id}/replace-from-funnel`, { funnelId });
}
